import { useEffect, useState } from "react";
import getConsumer from "../utils/actionCable";

// Live unread-notifications count for the staff navbar badge. Seeded from
// appProps.unread_notifications_count so the badge renders immediately, then kept
// current over NotificationsChannel (app/channels/notifications_channel.rb).
// The channel broadcasts { type: "notification", notification: { type, unread_count } }:
// an "initial_state" message carries the authoritative count, anything else is a
// new notification and bumps the count by one (mirrors the legacy NotificationsContext).
export default function useNotificationsCount(
  appProps,
  recipientType = "Staff",
  schoolSlug = undefined
) {
  const [count, setCount] = useState(appProps?.unread_notifications_count ?? 0);
  const userId = appProps?.user?.id;
  // Present only under the test harness (see app_props_for): scopes the
  // subscription to this caller's DB shard. Undefined drops out of the
  // serialized identifier, so production subscriptions are unchanged.
  const testDatabase = appProps?.test_database;

  useEffect(() => {
    if (!userId) return undefined;

    const consumer = getConsumer();
    if (!consumer) return undefined;

    const subscription = consumer.subscriptions.create(
      {
        channel: "NotificationsChannel",
        recipient_type: recipientType,
        // Staff pass the viewed school so the channel scopes the count to it;
        // guardian/student omit it (undefined drops out of the identifier) and
        // stay profile-wide, matching their unscoped appProps seed.
        school_slug: schoolSlug,
        test_database: testDatabase,
      },
      {
        received(data) {
          if (data?.type !== "notification") return;
          if (data.notification?.type === "initial_state") {
            setCount(data.notification.unread_count ?? 0);
          } else {
            setCount((prev) => prev + 1);
          }
        },
      }
    );

    return () => subscription.unsubscribe();
  }, [userId, recipientType, schoolSlug, testDatabase]);

  return count;
}
